import { electricalImage, tileImages } from '@/lib/media';
import { HREFLANG, LOCALES, localePath, type Locale } from '@/lib/site';

// The blog's post registry. Each post has a localized URL slug per locale.
// The localized copy lives in messages under `blog.posts.<id>`. Publication
// date + card image stay here so they never drift between locales. Images
// come from the local media manifest — no external URLs.
export interface BlogPostMeta {
  /** Canonical id — the key of the post's copy in messages `blog.posts`. */
  id: string;
  date: string;
  /** Set when the article content/meta is meaningfully updated after publication
   * — feeds BlogPosting dateModified + the sitemap lastmod. */
  lastModified?: string;
  image: string;
  slugs: Record<Locale, string>;
  /** In-article images, inserted after the section with the given index. */
  inlineImages?: { afterSection: number; image: string; portrait?: boolean }[];
}

export const BLOG_POSTS: BlogPostMeta[] = [
  {
    id: 'jaka-fuga-do-plytek-epoksydowa-czy-cementowa',
    date: '2026-08-09',
    // Mitred 45° tile corner with a hairline joint — the article's subject up close.
    image: tileImages[14],
    // Shower zone: hex mosaic with contrasting grout beside large format (portrait)
    // — after the expert-breakdown section on epoxy in wet zones.
    inlineImages: [{ afterSection: 1, image: tileImages[23], portrait: true }],
    slugs: {
      pl: 'jaka-fuga-do-plytek-epoksydowa-czy-cementowa',
      en: 'epoxy-vs-cement-grout-which-to-choose',
      ua: 'yaka-fuha-epoksydna-chy-tsementna',
      ru: 'kakaya-zatirka-epoksidnaya-ili-cementnaya',
    },
  },
  {
    id: 'elektryka-i-hydraulika-w-mieszkaniu-10-rozwiazan',
    date: '2026-08-10',
    // Distribution-board photo — the article's subject in one frame.
    image: electricalImage,
    // Bathroom tile photo (portrait) — after the concealed bidet-shower section.
    inlineImages: [{ afterSection: 5, image: tileImages[15], portrait: true }],
    slugs: {
      pl: 'elektryka-i-hydraulika-w-mieszkaniu-10-rozwiazan',
      en: 'apartment-electrical-plumbing-10-solutions',
      ua: 'elektryka-i-santekhnika-10-rishen',
      ru: 'elektrika-i-santehnika-10-reshenij',
    },
  },
  {
    id: 'top-5-bledow-przy-remoncie-mieszkania',
    date: '2026-08-10',
    image: tileImages[12],
    // Bathroom photo (portrait) — after the hidden-doors/concealed-plumbing
    // section, which is about exactly those installations.
    inlineImages: [{ afterSection: 1, image: tileImages[13], portrait: true }],
    slugs: {
      pl: 'top-5-bledow-przy-remoncie-mieszkania',
      en: 'top-5-apartment-renovation-mistakes',
      ua: 'top-5-pomylok-pid-chas-remontu-kvartyry',
      ru: 'top-5-oshibok-pri-remonte-kvartiry',
    },
  },
];

export const getPostByLocaleSlug = (locale: Locale, slug: string) =>
  BLOG_POSTS.find((post) => post.slugs[locale] === slug);

/** Match a slug from ANY locale — used to 301 e.g. /en/blog/<pl-slug> to the en slug. */
export const getPostByAnySlug = (slug: string) =>
  BLOG_POSTS.find((post) => Object.values(post.slugs).includes(slug));

/**
 * "Read also" picks: every post except the current one, in a pseudo-random
 * order seeded by the current post's id. Deterministic on purpose — the pages
 * are statically rendered, and a Math.random() pick would either freeze at
 * build time or flip visibly after hydration.
 */
export function getRelatedPosts(currentId: string, count = 2): BlogPostMeta[] {
  const hash = (s: string) => {
    let h = 5381;
    for (const c of s) h = (h * 33) ^ c.charCodeAt(0);
    return h >>> 0;
  };
  return BLOG_POSTS.filter((post) => post.id !== currentId)
    .sort((a, b) => hash(currentId + a.id) - hash(currentId + b.id))
    .slice(0, count);
}

/**
 * hreflang alternates for one post. Slugs differ per locale, so this is built
 * here instead of via site.ts altLanguages() (which assumes a shared suffix).
 */
export function postAltLanguages(post: BlogPostMeta): Record<string, string> {
  const path = (locale: Locale) => `${localePath(locale)}/blog/${post.slugs[locale]}`;
  const languages: Record<string, string> = { 'x-default': path('pl') };
  for (const locale of LOCALES) languages[HREFLANG[locale]] = path(locale);
  return languages;
}

// Localized "18 lipca 2026" date. `ua` is this site's segment for Ukrainian;
// map it to the BCP-47 tag `uk` so Intl resolves it.
export function formatPostDate(iso: string, locale: string) {
  const intlLocale = locale === 'ua' ? 'uk' : locale;
  return new Intl.DateTimeFormat(intlLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${iso}T00:00:00`));
}
